import React, { useContext, useState } from 'react';
import { T, useT, useTX } from '@transifex/react';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { Button } from 'src/antd';
import download from 'downloadjs';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import { SelectYearSimple } from 'src/ui/SelectMoment/SelectYearSimple';
import { SelectMonthSimple } from 'src/ui/SelectMoment/SelectMonthSimple';
import { Typography } from 'src/ui/Typography';

import { useGetToken } from '../../../../../api/config';
import { PropertyContext } from '../../../../../context/property-context';
import { formatDate } from '../../../../../utils';
import { GetCalendar } from './api';
import GeneratingCalendar from './generating';

import CompleteCalendar from './complete';

import { useRouting } from '../../../../../routing/child-router-factory';

const ResponsiveContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 0.5rem;
  column-gap: 0;
  margin-bottom: 3rem;

  @media screen and (min-width: 1200px) {
    flex-direction: row;
    column-gap: 2rem;
    row-gap: 0;
    margin-bottom: 0;
  }
`;

const SelectContainer = styled.div`
  min-width: 100%;

  @media screen and (min-width: 1200px) {
    min-width: 200px;
  }
`;

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  margin-bottom: 0;
  margin-top: 0.5rem;

  @media screen and (min-width: 1200px) {
    gap: 0;
    margin-bottom: 3rem;
  }
`;

const StyledSelectYear = styled(SelectYearSimple)`
  flex: 1;
  margin-right: 0.5rem;

  @media screen and (min-width: 1200px) {
    flex: none;
    margin-right: 1rem;
  }
`;

const StyledSelectMonth = styled(SelectMonthSimple)`
  flex: 1;
  width: 200px;

  @media screen and (min-width: 1200px) {
    flex: none;
  }
`;

const MainContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const TitleText = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 4rem;
`;

const DownloadCalendar = () => {
  const token = useGetToken();
  const { id: propertyId, location } = useContext(PropertyContext);

  const [fromYear, setFromYear] = useState(dayjs().year());
  const [toYear, setToYear] = useState(dayjs().year());
  const [toMonth, setToMonth] = useState(dayjs().month());
  const [fromMonth, setFromMonth] = useState(dayjs().month());
  const [pdfData, setPdfData] = useState();

  const history = useRouting.useHistory();

  // Reset the picker back to the current month and drop any generated PDF, so
  // the form is always fresh when re-shown. This view stays mounted across the
  // download → complete → form transition (the "Close"/"Cancel" paths re-show
  // the form without a remount), so the picker would otherwise keep the
  // previously selected month (BEACON-15). Setting the values before clearing
  // pdfData keeps everything in one batched render.
  const resetDownload = () => {
    const now = dayjs();
    setFromYear(now.year());
    setToYear(now.year());
    setFromMonth(now.month());
    setToMonth(now.month());
    setPdfData(undefined);
  };

  const t = useT();
  const tx = useTX();
  const cancelText = t('Cancel');
  const downloadText = t('Download');
  const collectionsString = t('Collections');

  const queryParameters = `api_key,${token}|from_date,${dayjs(
    `${fromYear}-${fromMonth + 1}-01`
  ).format('YYYY-MM-DD')}|to_date,${dayjs(`${toYear}-${toMonth + 1}-01`)
    .endOf('month')
    .format(
      'YYYY-MM-DD'
    )}|property_id,${propertyId}|language,${tx.getCurrentLocale()}`;

  const filename = `${collectionsString}_${location?.name}_${formatDate(
    dayjs(dayjs(`${fromYear}-01-01`).month(fromMonth))
  )}_${formatDate(dayjs())}.pdf`;

  const {
    data,
    refetch,
    isLoading: loading,
    isFetching: fetching,
  } = useQuery({
    queryKey: [
      'get-collection-calendar',
      fromYear,
      fromMonth,
      token,
      queryParameters,
    ],
    queryFn: () =>
      GetCalendar(token, filename, `calendar`, queryParameters, false),
    refetchOnWindowFocus: false,
    enabled: false,
  });

  // Fetch the calendar PDF on demand and trigger the browser download. This is
  // driven by the explicit button click (not a useEffect watching `data`):
  // useQuery has `enabled: false` but still serves cached data for a matching
  // queryKey, and a data-watching effect would re-fire on remount whenever the
  // current month matches a previously downloaded one — jumping straight to the
  // "completed" screen on reopen (BEACON-15).
  const handleDownload = async () => {
    const res = await refetch();
    // refetch() resolves (never rejects) and react-query keeps the last
    // successful `data` on a background error — so guard on isError too, or a
    // failed re-download would silently serve the previously cached PDF and
    // jump to the success screen.
    if (res.isError || !res.data) {
      toast.error(t('Could not generate the calendar. Please try again.'));
      return;
    }
    const response = res.data as $TSFixMe;
    setPdfData(response.data);
    const content = response.headers['content-type'];
    download(
      response.data,
      filename,
      typeof content === 'string' ? content : undefined
    );
  };

  return (
    <MainContainer>
      {!loading && !fetching && !pdfData && (
        <>
          <TitleText>
            <T _str="Please select date" />
          </TitleText>
          <ResponsiveContainer>
            <SelectContainer>
              <Typography weight="semi-bold">
                <T _str="From" />
              </Typography>
              <SelectWrapper>
                <StyledSelectYear
                  value={fromYear}
                  onChange={(e) => setFromYear(e)}
                  validRange={[2018, 2030]}
                />
                <StyledSelectMonth
                  onChange={(e) => setFromMonth(e)}
                  value={fromMonth}
                />
              </SelectWrapper>
            </SelectContainer>
            <SelectContainer>
              <Typography weight="semi-bold">
                <T _str="To" />
              </Typography>
              <SelectWrapper>
                <StyledSelectYear
                  value={toYear}
                  onChange={(e) => setToYear(e)}
                  validRange={[2018, 2030]}
                />
                <StyledSelectMonth
                  onChange={(e) => setToMonth(e)}
                  value={toMonth}
                />
              </SelectWrapper>
            </SelectContainer>
          </ResponsiveContainer>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <Button
              style={{ marginRight: '1rem' }}
              onClick={() => {
                resetDownload();
                history.goBack();
              }}
            >
              {cancelText}
            </Button>
            <Button type="primary" onClick={handleDownload}>
              {downloadText}
            </Button>
          </div>
        </>
      )}
      {(loading || fetching) && <GeneratingCalendar />}
      {pdfData && (
        <CompleteCalendar
          data={data}
          onClose={resetDownload}
          filename={filename}
        />
      )}
    </MainContainer>
  );
};

export default DownloadCalendar;
